/**
 * Event Schema Validator
 * Ensures Event JSON-LD meets Schema.org specification
 * and catches missing/invalid data before it reaches production
 */

export interface EventSchemaValidation {
  isValid: boolean;
  errors: string[];
  warnings: string[];
}

interface EventSchemaInput {
  id?: string;
  title?: string;
  description?: string;
  startDate?: string;
  endDate?: string;
  location?: {
    name?: string;
    address?: string;
    city?: string;
    postcode?: string;
    country?: string;
  };
  organiser?: {
    name?: string;
    url?: string;
  };
  imageUrl?: string;
  price?: number;
  currency?: string;
  eventUrl?: string;
}

export function validateEventSchema(event: unknown): EventSchemaValidation {
  const errors: string[] = [];
  const warnings: string[] = [];

  // Type guard and null check
  if (!event || typeof event !== 'object') {
    errors.push('Event object is missing');
    return { isValid: false, errors, warnings };
  }

  const typedEvent = event as EventSchemaInput;

  if (!typedEvent.title || typeof typedEvent.title !== 'string' || !typedEvent.title.trim()) {
    errors.push('Event title is required and must be a non-empty string');
  }

  if (!typedEvent.description || typeof typedEvent.description !== 'string' || !typedEvent.description.trim()) {
    errors.push('Event description is required and must be a non-empty string');
  }

  if (!typedEvent.startDate || typeof typedEvent.startDate !== 'string') {
    errors.push('Event startDate is required and must be a string in ISO 8601 format');
  } else {
    const startDate = new Date(typedEvent.startDate);
    if (isNaN(startDate.getTime())) {
      errors.push(`Event startDate is not valid ISO 8601: ${typedEvent.startDate}`);
    }
  }

  if (!typedEvent.eventUrl || typeof typedEvent.eventUrl !== 'string') {
    errors.push('Event eventUrl is required');
  } else if (!typedEvent.eventUrl.startsWith('http')) {
    errors.push(`Event eventUrl must be absolute URL, got: ${typedEvent.eventUrl}`);
  }

  // Optional fields - validate if present
  if (typedEvent.endDate) {
    if (typeof typedEvent.endDate !== 'string') {
      errors.push('Event endDate must be a string in ISO 8601 format');
    } else {
      const endDate = new Date(typedEvent.endDate);
      if (isNaN(endDate.getTime())) {
        errors.push(`Event endDate is not valid ISO 8601: ${typedEvent.endDate}`);
      }
    }
  }

  if (typedEvent.imageUrl) {
    if (typeof typedEvent.imageUrl !== 'string') {
      warnings.push('Event imageUrl should be a string');
    } else if (!typedEvent.imageUrl.startsWith('http')) {
      warnings.push(`Event imageUrl should be absolute URL, got: ${typedEvent.imageUrl}`);
    }
  }

  if (typedEvent.location) {
    if (typeof typedEvent.location !== 'object') {
      warnings.push('Event location should be an object');
    }
  }

  if (typedEvent.organiser) {
    if (typeof typedEvent.organiser !== 'object') {
      warnings.push('Event organiser should be an object');
    } else if (!typedEvent.organiser.name) {
      warnings.push('Event organiser.name is missing');
    }
  }

  if (typedEvent.price !== undefined) {
    if (typeof typedEvent.price !== 'number') {
      warnings.push('Event price should be a number');
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
  };
}

/**
 * Log validation errors/warnings for debugging
 * Only logs in development - silent in production
 */
export function logEventSchemaValidation(
  eventId: string,
  validation: EventSchemaValidation
): void {
  if (process.env.NODE_ENV !== 'development') {
    return;
  }

  if (!validation.isValid) {
    console.error(`[Event Schema] Validation failed for event ${eventId}:`, validation.errors);
  }

  if (validation.warnings.length > 0) {
    console.warn(`[Event Schema] Warnings for event ${eventId}:`, validation.warnings);
  }
}
